import React, { useState, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import MonthPicker from './MonthPicker';
import '../../../../styles/profile/components/add-education-modal.css';

interface AddEducationModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: EducationData) => void;
  title?: string;
  initialData?: any | null;
  isEditMode?: boolean;
}

interface EducationData {
  uid?: string;
  institutionName: string;
  degree: string;
  fieldOfStudy: string;
  graduationDate: string;
  description: string;
  grade: string;
  achievements: string[];
}

export default function AddEducationModal({ 
  open, 
  onClose, 
  onAdd,
  title = "Add Education",
  initialData = null,
  isEditMode = false
}: AddEducationModalProps) {
  const [institution, setInstitution] = useState('');
  const [degree, setDegree] = useState('Select degree type');
  const [degreeDropdownOpen, setDegreeDropdownOpen] = useState(false);
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [graduationDate, setGraduationDate] = useState<Date | null>(null);
  const [showGraduationCalendar, setShowGraduationCalendar] = useState(false);
  const [description, setDescription] = useState('');
  const [grade, setGrade] = useState('');
  const [achievements, setAchievements] = useState<string[]>([]);
  
  const degreeDropdownRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const degreeOptions = [
    'High School Diploma',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctoral Degree (Ph.D.)',
    'Professional Degree',
    'Certificate',
    'Other'
  ];

  const isFormValid = institution.trim() !== '' && degree !== 'Select degree type';

  useEffect(() => {
    if (open && initialData) {
      setInstitution(initialData.institution_name || '');
      setDegree(initialData.degree || 'Select degree type');
      setFieldOfStudy(initialData.field_of_study || '');
      setGraduationDate(initialData.graduation_date ? new Date(initialData.graduation_date) : null);
      setDescription(initialData.description || '');
      setGrade(initialData.grade || '');
      setAchievements(initialData.achievements || []);
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) {
      setInstitution('');
      setDegree('Select degree type');
      setFieldOfStudy('');
      setGraduationDate(null);
      setShowGraduationCalendar(false);
      setDescription('');
      setGrade('');
      setAchievements([]);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (degreeDropdownRef.current && !degreeDropdownRef.current.contains(e.target as Node)) {
        setDegreeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ graduation date';
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }

    const data: EducationData = {
      institutionName: institution,
      degree,
      fieldOfStudy,
      graduationDate: graduationDate ? graduationDate.toISOString() : '',
      description,
      grade,
      achievements: achievements.filter(a => a.trim() !== ''),
    };

    if (isEditMode && initialData?.uid) {
      data.uid = initialData.uid;
    }

    onAdd(data);
  };

  const handleAddAchievement = () => {
    setAchievements([...achievements, '']);
  };

  const handleRemoveAchievement = (index: number) => {
    setAchievements(achievements.filter((_, i) => i !== index));
  };

  const handleAchievementChange = (index: number, value: string) => {
    const newAchievements = [...achievements];
    newAchievements[index] = value;
    setAchievements(newAchievements);
  };

  if (!open) return null;

  return (
    <div
      className="add-education-modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="add-education-modal">
        <div className="add-education-modal-header">
          <h2 className="add-education-modal-title">{title}</h2>
        </div>

        <div className="add-education-modal-body">
          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">
              Institution
              <span className="add-education-modal-required">Required</span>
            </label>
            <input
              type="text"
              className="add-education-modal-input"
              placeholder="i.e. Florida State University, General Assembly"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
            />
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">
              Degree
              <span className="add-education-modal-required">Required</span>
            </label>
            <div className="add-education-modal-dropdown" ref={degreeDropdownRef}>
              <button
                type="button"
                className="add-education-modal-dropdown-trigger"
                onClick={() => setDegreeDropdownOpen(!degreeDropdownOpen)}
              >
                <span className="add-education-modal-dropdown-value">{degree}</span>
                <span className="add-education-modal-dropdown-arrow">▾</span>
              </button>
              {degreeDropdownOpen && (
                <div className="add-education-modal-dropdown-menu">
                  {degreeOptions.map((option) => (
                    <div
                      key={option}
                      className={`add-education-modal-dropdown-item ${degree === option ? 'add-education-modal-dropdown-item--selected' : ''}`}
                      onClick={() => {
                        setDegree(option);
                        setDegreeDropdownOpen(false);
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">Field of Study</label>
            <input
              type="text"
              className="add-education-modal-input"
              placeholder="Field of Study"
              value={fieldOfStudy}
              onChange={(e) => setFieldOfStudy(e.target.value)}
            />
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">Graduation Date</label>
            <div className="add-education-modal-date-wrapper">
              <button
                type="button"
                className="add-education-modal-date-btn"
                style={{ color: graduationDate ? '#1f2937' : '#9ca3af' }}
                onClick={() => setShowGraduationCalendar(!showGraduationCalendar)}
              >
                {formatDate(graduationDate)}
              </button>
              {showGraduationCalendar && (
                <MonthPicker
                  selectedDate={graduationDate}
                  onSelect={(date) => {
                    setGraduationDate(date);
                    setShowGraduationCalendar(false);
                  }}
                  onClose={() => setShowGraduationCalendar(false)}
                />
              )}
            </div>
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">Description</label>
            <textarea
              className="add-education-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">Grade</label>
            <input
              type="text"
              className="add-education-modal-input"
              placeholder="i.e: 3.5"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            />
          </div>

          <div className="add-education-modal-field-full">
            <label className="add-education-modal-label">Achievements</label>
            {achievements.map((achievement, index) => (
              <div key={index} className="add-education-modal-achievement-item">
                <input
                  type="text"
                  className="add-education-modal-input"
                  placeholder="Achievement"
                  value={achievement}
                  onChange={(e) => handleAchievementChange(index, e.target.value)}
                />
                <button
                  type="button"
                  className="add-education-modal-remove-btn"
                  onClick={() => handleRemoveAchievement(index)}
                  aria-label="Remove achievement"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="add-education-modal-add-achievement"
              onClick={handleAddAchievement}
            >
              <span className="add-education-modal-add-icon">+</span>
            </button>
          </div>
        </div>

        <div className="add-education-modal-footer">
          <button
            type="button"
            className="add-education-modal-btn add-education-modal-btn--discard"
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="button"
            className="add-education-modal-btn add-education-modal-btn--add"
            onClick={handleSubmit}
            disabled={!isFormValid}
            style={{
              opacity: !isFormValid ? 0.5 : 1,
              cursor: !isFormValid ? 'not-allowed' : 'pointer'
            }}
          >
            {isEditMode ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

